import React from 'react';
import { MilitaryHistoryItemModel } from 'forms/sections/military/history/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import * as formTypes from 'constants/formTypes';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import DateRange from 'components/Form/DateRange';
import Show from 'components/Form/Show';
import Country from 'components/Form/Country';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { countryValuesNoUS } from 'constants/enums/countries';

interface Props extends ValidationElementProps, Partial<MilitaryHistoryItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  className?: string
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  attribute?: string
}

export default class MilitaryService2013 extends ValidationElement<Props> {
  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  };

  update = (queue: Partial<MilitaryHistoryItemModel>) => {
    this.props.onUpdate({
      Service: this.props.Service,
      Status: this.props.Status,
      Officer: this.props.Officer,
      ServiceNumber: this.props.ServiceNumber,
      Dates: this.props.Dates,
      ServiceState: this.props.ServiceState,
      Country: this.props.Country,
      CountryFocused: this.props.CountryFocused,
      ...queue,
    });
  };

  isNonUSCountry = (values) => Array.isArray(values?.value) && countryValuesNoUS.includes(values.value[0]);

  updateCountry = (values: any) => {
    const shouldRemoveState = this.shouldDisplayServiceState() && this.isNonUSCountry(values);
    const additionalFields = shouldRemoveState ? { ServiceState: {} } : {};

    this.update({
      Country: values,
      ...additionalFields,
    });
  };

  updateService = (values: MilitaryHistoryItemModel['Service']) => {
    this.update({
      Service: values,
      ServiceState: ['NationalGuard'].includes(values.value) ? this.props.ServiceState : {},
    });
  };

  updateStatus = (values: MilitaryHistoryItemModel['Status']) => {
    this.update({
      Status: values,
    });
  };

  updateOfficer = (values: MilitaryHistoryItemModel['Officer']) => {
    this.update({
      Officer: values,
    });
  };

  updateServiceNumber = (value: MilitaryHistoryItemModel['ServiceNumber']) => {
    this.update({
      ServiceNumber: value,
    });
  };

  updateDates = (value: MilitaryHistoryItemModel['Dates']) => {
    this.update({
      Dates: value,
    });
  };

  updateServiceState = (value: MilitaryHistoryItemModel['ServiceState']) => {
    this.update({
      ServiceState: value,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      CountryFocused: focus,
    });
  };

  shouldDisplayServiceState = () => (
    this.props?.Service?.value === 'NationalGuard'
    && (!this.props?.Country?.value || !this.isNonUSCountry(this.props?.Country))
  );

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <div className="military-service">
        <Field
          title={i18n.t('military.history.heading.service')}
          adjustFor="big-buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Service`}
          exactAttribute
        >
          <RadioGroup
            className="service option-list eapp-extend-labels option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.Service || {}).value}
            attribute={`${attribute}.Service`}
          >
            <Radio
              name="service-airforce"
              className="service-airforce"
              label={i18n.t('military.history.label.airforce')}
              value="AirForce"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
            <Radio
              name="service-army"
              className="service-army"
              label={i18n.t('military.history.label.army')}
              value="Army"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
            <Radio
              name="service-coastguard"
              className="service-coastguard"
              label={i18n.t('military.history.label.coastguard')}
              value="CoastGuard"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
            <Radio
              name="service-marinecorps"
              className="service-marinecorps"
              label={i18n.t('military.history.label.marinecorps')}
              value="MarineCorps"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
            <Radio
              name="service-navy"
              className="service-navy"
              label={i18n.t('military.history.label.navy')}
              value="Navy"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
            <Radio
              name="service-nationalguard"
              className="service-nationalguard"
              label={i18n.t('military.history.label.nationalguard')}
              value="NationalGuard"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
            <Radio
              name="service-merchantmarine"
              className="service-merchantmarine"
              label={i18n.t('military.history.label.merchantmarine')}
              value="MerchantMarine"
              onUpdate={this.updateService}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <Field
          title={i18n.t('military.history.heading.status')}
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Status`}
        >
          <RadioGroup
            className="status option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.Status || {}).value}
            attribute={`${attribute}.Status`}
          >
            <Radio
              name="status-activeduty"
              className="status-activeduty"
              label={i18n.t('military.history.label.activeduty')}
              value="ActiveDuty"
              onUpdate={this.updateStatus}
              onError={this.props.onError}
            />
            <Radio
              name="status-activereserve"
              className="status-activereserve"
              label={i18n.t('military.history.label.activereserve')}
              value="ActiveReserve"
              onUpdate={this.updateStatus}
              onError={this.props.onError}
            />
            <Radio
              name="status-inactivereserve"
              className="status-inactivereserve"
              label={i18n.t('military.history.label.inactivereserve')}
              value="InactiveReserve"
              onUpdate={this.updateStatus}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <Field
          title={i18n.t('military.history.heading.officer')}
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Officer`}
        >
          <RadioGroup
            className="officer option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.Officer || {}).value}
            attribute={`${attribute}.Officer`}
          >
            <Radio
              name="officer-officer"
              className="officer-officer"
              label={i18n.t('military.history.label.officer')}
              value="Officer"
              onUpdate={this.updateOfficer}
              onError={this.props.onError}
            />
            <Radio
              name="officer-enlisted"
              className="officer-enlisted"
              label={i18n.t('military.history.label.enlisted')}
              value="Enlisted"
              onUpdate={this.updateOfficer}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <Field
          title={i18n.t('military.history.heading.number')}
          help="military.history.help.number"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ServiceNumber`}
        >
          {i18n.m('military.selective.heading.message')}
          <Text
            name="ServiceNumber"
            {...this.props.ServiceNumber}
            className="service-number"
            onUpdate={this.updateServiceNumber}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.ServiceNumber`}
          />
        </Field>

        <Field
          title={i18n.t('military.history.heading.dates')}
          help="military.history.help.dates"
          adjustFor="daterange"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            name="Dates"
            className="dates"
            {...this.props.Dates}
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
          />
        </Field>

        <Show when={this.shouldDisplayServiceState()}>
          <Field
            title={i18n.t('military.history.heading.servicestate')}
            adjustFor="label"
            help="military.history.help.nationalguardserved"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ServiceState`}
          >
            <Location
              name="ServiceState"
              className="service-state"
              {...this.props.ServiceState}
              layout={Location.STATE}
              onUpdate={this.updateServiceState}
              onError={this.props.onError}
              required={!this.props.Country?.value}
              attribute={`${attribute}.ServiceState`}
            />
          </Field>
        </Show>
        <Show when={this.props.formType === formTypes.SF852013}>
          <Field
            title={i18n.t('military.history.heading.country')}
            help="military.history.help.country"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Country`}
          >
            <Country
              name="Country"
              multiple={false}
              className="country"
              {...this.props.Country}
              onUpdate={this.updateCountry}
              updateCountryFocused={this.updateCountryFocused}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Country`}
              excludeUnitedStates={false}
            />
          </Field>
        </Show>
      </div>
    );
  }
}

MilitaryService2013.defaultProps = {
  Service: {},
  Status: {},
  Officer: {},
  ServiceNumber: {},
  Dates: {},
  ServiceState: {},
  Country: {},
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  CountryFocused: false,
};
